/*
  QuickSmsDialog — one-tap SMS from a client row.

  - Pre-fills recipient phone from the row (client_phone) if present.
  - Loads all SMS templates so operator can pick one; template body is
    editable before send. Simple {name} merge tag replaced client-side.
  - Uses the existing /api/sms/send endpoint (single message batch).
*/
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MessageSquare, Send, Loader2 } from "lucide-react";

function applyMergeTags(body, { client_name }) {
  return (body || "")
    .replace(/\{name\}/gi, (client_name || "").split(" ")[0] || "there")
    .replace(/\{full_name\}/gi, client_name || "");
}

export default function QuickSmsDialog({ open, onClose, clientEmail, clientName, clientPhone }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [phone, setPhone] = useState(clientPhone || "");
  const [name, setName] = useState(clientName || "");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhone(clientPhone || "");
    setName(clientName || "");
    setBody("");
    setTemplateId("");
    api.get("/sms/templates")
      .then((r) => setTemplates(r.data || []))
      .catch(() => setTemplates([]));
  }, [open, clientPhone, clientName]);

  const preview = useMemo(() => applyMergeTags(body, { client_name: name }), [body, name]);
  const charCount = preview.length;
  const smsParts = Math.max(1, Math.ceil(charCount / 160));

  function pickTemplate(id) {
    setTemplateId(id);
    if (!id) return;
    const t = templates.find((x) => x.id === id || x._id === id);
    if (t) setBody(t.body || "");
  }

  async function send() {
    if (!phone.trim()) return toast.error("Phone number is required");
    if (!body.trim()) return toast.error("Message body is required");
    setSending(true);
    try {
      const r = await api.post("/sms/send", {
        messages: [{
          to: phone.trim(),
          body: preview,
          recipient_name: name.trim() || undefined,
          customer_key: `email:${(clientEmail || "").toLowerCase()}`,
        }],
      });
      const res = (r.data.results || [])[0] || {};
      if (res.status && res.status !== "SUCCESS") {
        toast.error("SMS failed", { description: res.error_text || res.status });
      } else {
        toast.success("SMS sent", { description: `To ${phone}` });
        onClose?.();
      }
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSending(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="quick-sms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" /> Send SMS
          </DialogTitle>
          <DialogDescription>
            {name ? `To ${name}` : "Quick send"} · via ClickSend
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Recipient name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Client name" data-testid="quick-sms-name" />
          </div>
          <div>
            <Label className="text-xs">Recipient phone</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+61…"
              className="font-mono-num"
              data-testid="quick-sms-phone"
            />
            {!clientPhone && (
              <div className="text-2xs text-amber-700 mt-1">
                No phone on file — enter one manually.
              </div>
            )}
          </div>
          {templates.length > 0 && (
            <div>
              <Label className="text-xs">Template (optional)</Label>
              <select
                value={templateId}
                onChange={(e) => pickTemplate(e.target.value)}
                className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm"
                data-testid="quick-sms-template-select"
              >
                <option value="">— Freeform —</option>
                {templates.map((t) => (
                  <option key={t.id || t._id} value={t.id || t._id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Hi {name}, just a quick reminder…"
              className="min-h-[100px] text-sm"
              data-testid="quick-sms-body"
            />
            <div className="mt-1 text-2xs text-zinc-500 flex justify-between">
              <span>{charCount} chars · {smsParts} part{smsParts > 1 ? "s" : ""}</span>
              <span>Merge tags: {"{name}"} {"{full_name}"}</span>
            </div>
          </div>
          {preview && preview !== body && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs">
              <div className="font-semibold text-zinc-500 uppercase tracking-wider text-2xs">Preview</div>
              <div className="mt-0.5 whitespace-pre-wrap text-zinc-800">{preview}</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending || !phone.trim() || !body.trim()} data-testid="quick-sms-send">
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
